import { create } from 'zustand';
import type { Organization, OrgMember } from '@/types/database.types';
import type { OrgTheme } from '@/types/api';

interface OrgState {
  organization: Organization | null;
  memberRole: OrgMember['role'] | null;
  isLoading: boolean;
  error: string | null;
  setOrg: (org: Organization, role: OrgMember['role']) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clear: () => void;
}

export const useOrgStore = create<OrgState>((set) => ({
  organization: null,
  memberRole: null,
  isLoading: true,
  error: null,
  setOrg: (organization, memberRole) =>
    set({ organization, memberRole, isLoading: false, error: null }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) =>
    set((state) => ({ error, isLoading: error ? false : state.isLoading })),
  clear: () =>
    set({ organization: null, memberRole: null, isLoading: false, error: null }),
}));

/** Apply org brand theme to CSS variables on the document root */
export function applyOrgTheme(theme: OrgTheme | null) {
  const root = document.documentElement;

  // Reset brand variables
  root.style.removeProperty('--brand');
  root.style.removeProperty('--brand-soft');
  root.style.removeProperty('--brand-border');

  if (!theme?.brand) return;

  root.style.setProperty('--brand', theme.brand);

  if (theme.brandSoft) {
    root.style.setProperty('--brand-soft', theme.brandSoft);
  } else {
    // Derive from brand hex
    const hex = theme.brand.replace('#', '');
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      root.style.setProperty('--brand-soft', `rgba(${r},${g},${b},0.14)`);
      root.style.setProperty('--brand-border', `rgba(${r},${g},${b},0.35)`);
    }
  }

  if (theme.brandBorder) {
    root.style.setProperty('--brand-border', theme.brandBorder);
  }
}
